import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { useArticles } from '../contexts/ArticleContext';
import { categories } from '../data/categories';

// Hamburger button
const HamburgerButton = styled.button`
  position: fixed;
  top: 18px;
  right: 10px;
  display: none;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 5px;
  padding: 8px;
  z-index: 101;
  cursor: pointer;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
  }
`;

// ハンバーガーアイコン用の線
const MenuLine = styled.span`
  display: block;
  width: 100%;
  height: 3px;
  background-color: black;
  transition: all 0.3s ease;
`;

// ×印ボタン
const CloseButton = styled.button`
  position: fixed;
  top: 18px;
  right: 10px;
  display: none;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  border-radius: 5px;
  padding: 8px;
  z-index: 101;
  cursor: pointer;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 24px;
    height: 3px;
    background-color: black;
  }
  
  &::before {
    transform: rotate(45deg);
  }
  
  &::after {
    transform: rotate(-45deg);
  }
`;

// Mobile menu overlay
const MenuOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: ${props => props.isOpen ? '0' : '-100%'};
  width: 80%;
  max-width: 360px;
  height: 100%;
  background-color: white;
  z-index: 100;
  transition: right 0.3s ease-in-out;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.2);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

// Menu container
const MenuContainer = styled.div`
  padding: 60px 15px 80px;
  min-height: 100%;
  box-sizing: border-box;
`;

// Category Title
const CategoryTitle = styled.h2`
  color: ${theme.colors.primary};
  font-size: 1.2rem;
  margin: 20px 0 10px;
  padding-bottom: 5px;
  border-bottom: 1px solid ${theme.colors.primary};
  
  &:first-child {
    margin-top: 0;
  }
`;

// Menu list
const MenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

// Menu item
const MenuItem = styled.li`
  margin: 5px 0;
`;

// Menu link
const MenuLink = styled(Link)`
  display: block;
  padding: 6px 10px;
  color: #333;
  text-decoration: none;
  font-size: 0.95rem;
  
  &:hover {
    background-color: #f5f5f5;
  }

  &::before {
    content: '•';
    display: inline-block;
    margin-right: 8px;
    color: ${theme.colors.primary};
  }
`;

// Country menu link with extra indentation
const CountryMenuLink = styled(MenuLink)`
  padding-left: 25px;
  
  &::before {
    content: '•';
    font-size: 0.8rem;
  }
`;

// Overlay mask
const OverlayMask = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99;
  display: ${props => props.isOpen ? 'block' : 'none'};
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    display: none;
  }
`;

const MobileMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { articles } = useArticles();

  // 新着記事を日付順にソートし、最大5件に制限
  const latestArticles = [...articles]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 5);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Prevent body scrolling when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  const openMenu = () => {
    setIsOpen(true);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  // メニューを閉じてスクロールを一番上にリセットする関数
  const handleMenuItemClick = () => {
    setIsOpen(false);
    window.scrollTo(0, 0);
  };

  return (
    <>
      {/* メニューが閉じているときだけハンバーガーボタンを表示 */}
      {!isOpen && (
        <HamburgerButton 
          onClick={openMenu}
          aria-label="メニューを開く"
        >
          <MenuLine />
          <MenuLine />
          <MenuLine />
        </HamburgerButton>
      )}
      
      {/* メニューが開いているときだけ閉じるボタンを表示 */}
      {isOpen && (
        <CloseButton 
          onClick={closeMenu}
          aria-label="メニューを閉じる"
        />
      )}
      
      <MenuOverlay isOpen={isOpen} ref={menuRef}>
        <MenuContainer>
          <CategoryTitle>新着記事</CategoryTitle>
          <MenuList>
            {latestArticles.length > 0 ? (
              latestArticles.map(article => (
                <MenuItem key={article.slug}>
                  <MenuLink to={`/article/${article.slug}`} onClick={handleMenuItemClick}>
                    {article.title}
                  </MenuLink>
                </MenuItem>
              ))
            ) : (
              <MenuItem>
                <MenuLink to="/articles" onClick={handleMenuItemClick}>記事一覧</MenuLink>
              </MenuItem>
            )}
          </MenuList>
          
          {/* 自動的にカテゴリを生成 */}
          {categories.map(category => (
            <React.Fragment key={category.id}>
              <CategoryTitle>{category.name}</CategoryTitle>
              <MenuList>
                <MenuItem>
                  <MenuLink to={`/category/${category.name}`} onClick={handleMenuItemClick}>
                    {category.name}の記事一覧
                  </MenuLink>
                </MenuItem>
                {category.subCategories.length > 0 && category.subCategories.map(subCategory => (
                  <MenuItem key={subCategory}>
                    <CountryMenuLink to={`/category/${category.name}/${subCategory}`} onClick={handleMenuItemClick}>
                      {subCategory}
                    </CountryMenuLink>
                  </MenuItem>
                ))}
              </MenuList>
            </React.Fragment>
          ))}
                    
        </MenuContainer>
      </MenuOverlay>
      
      <OverlayMask isOpen={isOpen} onClick={() => setIsOpen(false)} />
    </>
  );
};

export default MobileMenu; 